import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Search, Plus, RefreshCw, Trash2, Eye } from "lucide-react";
import { toast } from "react-toastify";
import {
  listContacts,
  deleteContact,
} from "../../apis/contacts";
import { listContactLists } from "../../apis/contactLists";
import ContactDrawer from "../../components/contacts/ContactDrawer";
import ContactDetailsDrawer from "../../components/contacts/ContactDetailsDrawer";
import BulkActionBar from "../../components/contacts/BulkActionBar";

const PAGE_SIZE = 50;

const STATUS_OPTIONS = ["", "active", "inactive", "blocked"];
const SOURCE_OPTIONS = ["", "csv", "manual", "api", "broadcast", "inbound", "dynamic_segment"];

const ContactsListPage = ({ phoneId }) => {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [listFilter, setListFilter] = useState("");
  const [lists, setLists] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContact, setDrawerContact] = useState(null);
  const [detailsId, setDetailsId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPage = useCallback(async () => {
    if (!phoneId) return;
    setLoading(true);
    try {
      const params = {
        phone_id: phoneId,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (status) params.status = status;
      if (source) params.source = source;
      if (listFilter) params.list_id = listFilter;
      const r = await listContacts(params);
      setContacts(r.data.contacts || []);
      setTotal(r.data.pagination?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [phoneId, page, debouncedSearch, status, source, listFilter]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  useEffect(() => {
    if (!phoneId) return;
    listContactLists({ phone_id: phoneId, limit: 200 })
      .then((r) => setLists(r.data.lists || []))
      .catch(() => {});
  }, [phoneId]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  const allSelected = contacts.length > 0 && contacts.every((c) => selectedIds.includes(c.id));

  const toggleAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(contacts.map((c) => c.id));
  };

  const onDeleteOne = async (id) => {
    if (!window.confirm("Delete this contact? This cannot be undone.")) return;
    try {
      await deleteContact(id, phoneId);
      toast.success("Contact deleted");
      fetchPage();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete");
    }
  };

  const openDrawer = (contact) => {
    setDrawerContact(contact);
    setDrawerOpen(true);
  };

  const openDetails = (contact) => {
    setDetailsId(contact.id);
  };

  const onDrawerSaved = () => {
    setDrawerOpen(false);
    setDrawerContact(null);
    fetchPage();
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search name, phone, email or company"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="text-sm border border-gray-200 rounded-md px-3 py-2 bg-white"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s ? s : "All statuses"}</option>
            ))}
          </select>
          <select
            value={source}
            onChange={(e) => { setSource(e.target.value); setPage(1); }}
            className="text-sm border border-gray-200 rounded-md px-3 py-2 bg-white"
          >
            {SOURCE_OPTIONS.map((s) => (
              <option key={s} value={s}>{s ? s.replace("_", " ") : "All sources"}</option>
            ))}
          </select>
          <select
            value={listFilter}
            onChange={(e) => { setListFilter(e.target.value); setPage(1); }}
            className="text-sm border border-gray-200 rounded-md px-3 py-2 bg-white"
          >
            <option value="">All lists</option>
            {lists.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
          <div className="ml-auto flex gap-2">
            <button
              onClick={fetchPage}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={() => openDrawer({})}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
            >
              <Plus size={14} /> New contact
            </button>
          </div>
        </div>

        <BulkActionBar
          phoneId={phoneId}
          selectedIds={selectedIds}
          lists={lists}
          onClear={() => setSelectedIds([])}
          onMutated={fetchPage}
        />

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2.5 w-10">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                </th>
                <th className="px-3 py-2.5 text-left">Name</th>
                <th className="px-3 py-2.5 text-left">Phone</th>
                <th className="px-3 py-2.5 text-left">Email</th>
                <th className="px-3 py-2.5 text-left">Company</th>
                <th className="px-3 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 text-left">Source</th>
                <th className="px-3 py-2.5 text-left">Last activity</th>
                <th className="px-3 py-2.5 w-20" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : contacts.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-12 text-center text-gray-400">No contacts match these filters.</td></tr>
              ) : contacts.map((c) => (
                <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(c.id)}
                      onChange={() => {
                        setSelectedIds((s) =>
                          s.includes(c.id) ? s.filter((x) => x !== c.id) : [...s, c.id]
                        );
                      }}
                    />
                  </td>
                  <td className="px-3 py-2.5 font-medium text-gray-900">
                    <button className="hover:underline text-left" onClick={() => openDetails(c)}>
                      {c.full_name || c.first_name || c.whatsapp_name || "—"}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-gray-700">{c.country_code ? `+${c.country_code} ` : ""}{c.phone}</td>
                  <td className="px-3 py-2.5 text-gray-600">{c.email || "—"}</td>
                  <td className="px-3 py-2.5 text-gray-600">{c.company_name || "—"}</td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      c.status === "active" ? "bg-emerald-50 text-emerald-700" :
                      c.status === "blocked" ? "bg-rose-50 text-rose-700" :
                      "bg-gray-100 text-gray-700"
                    }`}>{c.status}</span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 capitalize">{(c.source || "").replace("_", " ")}</td>
                  <td className="px-3 py-2.5 text-gray-600">
                    {c.last_activity_at ? new Date(c.last_activity_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button
                      onClick={() => openDetails(c)}
                      title="View details"
                      className="p-1.5 rounded text-gray-600 hover:bg-gray-100"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      onClick={() => onDeleteOne(c.id)}
                      title="Delete"
                      className="p-1.5 rounded text-rose-600 hover:bg-rose-50"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm">
          <span className="text-gray-500">
            {loading ? "Loading…" : `${total.toLocaleString()} contact${total === 1 ? "" : "s"}`}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 border border-gray-200 rounded text-gray-700 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-gray-500">Page {page} / {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1 border border-gray-200 rounded text-gray-700 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <ContactDrawer
          phoneId={phoneId}
          contact={drawerContact}
          onClose={() => setDrawerOpen(false)}
          onSaved={onDrawerSaved}
        />
      )}

      {detailsId && (
        <ContactDetailsDrawer
          phoneId={phoneId}
          contactId={detailsId}
          onClose={() => setDetailsId(null)}
          onEdit={(c) => {
            setDetailsId(null);
            openDrawer(c);
          }}
          onDeleted={() => {
            setDetailsId(null);
            fetchPage();
          }}
        />
      )}
    </div>
  );
};

export default ContactsListPage;
